import { addDays, addMonths, addWeeks, addYears, addHours, addMinutes, addSeconds, addMilliseconds, format, toDate, parse as dnfParse, isValid, isBefore, isAfter, differenceInMilliseconds, differenceInSeconds, differenceInMinutes, differenceInHours, differenceInDays, differenceInWeeks, differenceInMonths, differenceInYears, parseISO, getTime, startOfMonth, endOfMonth, isEqual, startOfDay, endOfDay } from 'date-fns/fp'

/**
 * Wrapper API for date-fns
 *
 *
 *
 * @type {{MILLISECONDS: string, before(*=, *=): boolean, SECONDS: string, DAYS: string, parse(*=, *=): ChronosInstance, MONTHS: string, YEARS: string, parseExact(*=, *=, *=): ChronosInstance, HOURS: string, WEEKS: string, now(): ChronosInstance, MINUTES: string, after(*=, *=): boolean}}
 */
export const Chronos = {

    MILLISECONDS : "milliseconds",
    SECONDS : "seconds",
    MINUTES : "minutes",
    HOURS : "hours",
    DAYS : "days",
    WEEKS : "weeks",
    MONTHS : "months",
    YEARS : "years",

    now() {
        return new ChronosInstance(new Date());
    },

    with(date) {
        if (isValid(date)) {
            return new ChronosInstance(date);
        }
        return null;
    },

    withTimestampSeconds(input) {
        try {
            let date = new Date(parseInt(input) * 1000);
            return this.with(date);
        } catch (e) {
            console.log(e);
        }
        return this.now();
    },

    withTimestamp(input) {
        try {
            let date = new Date(parseInt(input));
            return this.with(date);
        } catch (e) {
            console.log(e);
        }
        return this.now();
    },

    parse(input, options) {
        try {
            return new ChronosInstance(parseISO(input), options);
        } catch (e) {
            console.log(e);
        }
        return this.now();
    },

    parseExact(input, format, options) {
        try {
            let parsedDate;
            if (this.isNumeric(input)) {
                console.log("CHRONOS WAS NUMERIC");
                parsedDate = dnfParse(parseInt(input));
            } else {
                console.log("CHRONOS WAS NOT NUMERIC");
                parsedDate = dnfParse(input, format);
            }
            return new ChronosInstance(parsedDate, options);
        } catch (e) {
            console.log(e);
        }
        return this.now();
    },

    before(date1, date2) {
        return isBefore(date2, date1);
    },

    after(date1, date2) {
        return isAfter(date2, date1);
    },

    isNumeric(str) {
        if (typeof str != "string") return false // we only process strings!
        return !isNaN(str) && // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
            !isNaN(parseFloat(str)) // ...and ensure strings of whitespace fail
    },

    formatTime(seconds) {
        let hours = Math.floor(seconds / 3600);
        seconds %= 3600;
        let minutes = Math.floor(seconds / 60);
        seconds = Math.floor(seconds % 60);

        let out = "";
        if (hours > 0) {
            if (hours < 10) {
                out += "0";
            }
            out += hours + ":";
        }

        if (minutes > 0) {
            if (minutes < 10) {
                out += "0";
            }
            out += minutes + ":";
        } else {
            out += "00:";
        }

        if (seconds < 10) {
            out += "0";
        }

        return out + seconds;
    }

}

export default class ChronosInstance {

    date = undefined;
    options = undefined;

    constructor(date, options) {
        this.date = date;
        this.options = options;
    }

    add = (value, unit) => {
        switch (unit) {
            case Chronos.MILLISECONDS:
                this.date = addMilliseconds(value, this.date); break;
            case Chronos.SECONDS:
                this.date = addSeconds(value, this.date); break;
            case Chronos.MINUTES:
                this.date = addMinutes(value, this.date); break;
            case Chronos.HOURS:
                this.date = addHours(value, this.date); break;
            case Chronos.DAYS:
                this.date = addDays(value, this.date); break;
            case Chronos.WEEKS:
                this.date = addWeeks(value, this.date); break;
            case Chronos.MONTHS:
                this.date = addMonths(value, this.date); break;
            case Chronos.YEARS:
                this.date = addYears(value, this.date); break;
            default:
                console.log("UNKNOWN UNIT");
        }

        return this;
    }

    difference = (date, unit) => {
        switch (unit) {
            case Chronos.MILLISECONDS:
                return differenceInMilliseconds(date, this.date);
            case Chronos.SECONDS:
                return differenceInSeconds(date, this.date);
            case Chronos.MINUTES:
                return differenceInMinutes(date, this.date);
            case Chronos.HOURS:
                return differenceInHours(date, this.date);
            case Chronos.DAYS:
                return differenceInDays(date, this.date);
            case Chronos.WEEKS:
                return differenceInWeeks(date, this.date);
            case Chronos.MONTHS:
                return differenceInMonths(date, this.date);
            case Chronos.YEARS:
                return differenceInYears(date, this.date);
        }

        return 0;
    }


    startOfDay = () => {
        return Chronos.with(startOfDay(this.date));
    }

    endOfDay = () => {
        return Chronos.with(endOfDay(this.date));
    }

    // startOfDay = () => {
    //     return Chronos.with(this.date)
    //         .add((this.date.getHours() * -1), Chronos.HOURS)
    //         .add((this.date.getMinutes() * -1), Chronos.MINUTES)
    //         .add((this.date.getSeconds() * -1), Chronos.SECONDS)
    //         .add((this.date.getMilliseconds() * -1), Chronos.MILLISECONDS)
    //         .getDate();
    // }

    startOfMonth = () => {
        let som = startOfMonth(this.date);
        return Chronos.with(som);
    }

    endOfMonth = () => {
        let eom = endOfMonth(this.date);
        return Chronos.with(eom);
    }

    is = (date) => {
        return isEqual(this.date, date);
    }

    isSameDay = (date) => {
        let sDate = Chronos.parse(Chronos.with(this.date).format("yyyy-MM-dd"));
        let sIn = Chronos.parse(Chronos.with(date).format("yyyy-MM-dd"));

        return isEqual(sDate.getDate(), sIn.getDate());
    }

    isAfter = (date) => {
        return isAfter(date, this.date);
    }

    isBefore = (date) => {
        return isBefore(date, this.date);
    }

    format = (formatString, options) => {
        if (options === undefined) {
            options = this.options;
        }

        return format(formatString, this.date);
    }

    getDate = () => {
        return this.date;
    }

    isValid = () => {
        if (this.date !== null) {
            return isValid(this.date);
        }
        return false;
    }

    seconds = () => {
        return (getTime(this.date) / 1000).toFixed(0);
    }

    milliseconds = () => {
        return getTime(this.date);
    }

}